import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { useApp } from '@/context/AppContext';
import api, { errMsg } from '@/lib/api';
import { toast } from 'sonner';
import { FIELD_TYPES, typeMeta } from '@/lib/customFields';
import { SlidersHorizontal, Plus, Pencil, Trash2, Save, X } from 'lucide-react';

const blankField = () => ({
  id: null, name: '', type: 'text', required: false,
  options: [], applies_to: { inventory: true, billing: false }, active: true,
});

export function CustomFieldsPanel() {
  const { user } = useApp();
  const isOwner = user?.role === 'OWNER' || user?.owner_access;
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState(blankField());
  const [editIdx, setEditIdx] = useState(-1);
  const [optText, setOptText] = useState('');

  const load = async () => {
    setLoading(true);
    try { setFields((await api.get('/custom-fields')).data.data || []); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (isOwner) load(); }, [isOwner]);

  const openAdd = () => { setDraft(blankField()); setEditIdx(-1); setOptText(''); setEditOpen(true); };
  const openEdit = (f, idx) => { setDraft({ ...blankField(), ...f, applies_to: { ...f.applies_to } }); setEditIdx(idx); setOptText(''); setEditOpen(true); };

  const commitDraft = () => {
    if (!draft.name.trim()) { toast.error('Enter a field name.'); return; }
    if (draft.type === 'dropdown' && (!draft.options || draft.options.length === 0)) {
      toast.error('Add at least one dropdown option.'); return;
    }
    if (!draft.applies_to.inventory && !draft.applies_to.billing) {
      toast.error('Choose where this field applies (Inventory and/or Billing).'); return;
    }
    const next = [...fields];
    if (editIdx >= 0) next[editIdx] = { ...draft };
    else next.push({ ...draft });
    setFields(next); setDirty(true); setEditOpen(false);
  };

  const removeField = (idx) => { setFields((f) => f.filter((_, i) => i !== idx)); setDirty(true); };
  const move = (idx, dir) => {
    const j = idx + dir; if (j < 0 || j >= fields.length) return;
    const next = [...fields]; [next[idx], next[j]] = [next[j], next[idx]];
    setFields(next); setDirty(true);
  };
  const toggleActive = (idx) => { setFields((f) => f.map((x, i) => i === idx ? { ...x, active: !(x.active !== false) } : x)); setDirty(true); };

  const saveAll = async () => {
    setSaving(true);
    try {
      const r = await api.put('/custom-fields', { fields: fields.map((f, i) => ({ ...f, order: i })) });
      setFields(r.data.data || []); setDirty(false);
      toast.success('Custom fields saved');
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const addOption = () => {
    const v = optText.trim(); if (!v) return;
    if ((draft.options || []).includes(v)) { toast.error('Option already added.'); return; }
    setDraft((d) => ({ ...d, options: [...(d.options || []), v] })); setOptText('');
  };
  const removeOption = (o) => setDraft((d) => ({ ...d, options: (d.options || []).filter((x) => x !== o) }));

  if (!isOwner) return null;

  return (
    <div className="rounded-2xl border border-border bg-card p-4 sc-card" data-testid="custom-fields-panel">
      <div className="mb-1 flex items-center gap-2">
        <SlidersHorizontal className="h-4 w-4 text-primary" /><p className="text-sm font-semibold">Custom fields</p>
        <span className="ml-auto rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-secondary-foreground">Owner only</span>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">Build your own fields (e.g. Tailor, Delivery date, Reference photo) and choose whether they appear when adding inventory, during billing, or both. Photo fields open the camera and store the image securely.</p>

      {loading ? (
        <p className="py-4 text-center text-xs text-muted-foreground">Loading…</p>
      ) : fields.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border py-8 text-center">
          <p className="text-sm font-medium">No custom fields yet</p>
          <p className="mt-1 text-xs text-muted-foreground">Add your first field to capture extra details.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {fields.map((f, idx) => {
            const Icon = typeMeta(f.type).icon;
            return (
              <div key={f.id || f.key || idx} className="flex items-center gap-2 rounded-xl border border-border p-2.5" data-testid={`cf-row-${idx}`}>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-secondary text-muted-foreground"><Icon className="h-4 w-4" /></span>
                <div className="min-w-0 flex-1">
                  <p className={`truncate text-sm font-medium ${f.active === false ? 'text-muted-foreground line-through' : ''}`}>{f.name}</p>
                  <div className="mt-0.5 flex flex-wrap items-center gap-1">
                    <Badge>{typeMeta(f.type).label}</Badge>
                    {f.required && <Badge tone="amber">Required</Badge>}
                    {f.applies_to?.inventory && <Badge tone="emerald">Inventory</Badge>}
                    {f.applies_to?.billing && <Badge tone="indigo">Billing</Badge>}
                  </div>
                </div>
                <div className="flex items-center gap-0.5">
                  <button onClick={() => move(idx, -1)} className="rounded-md border border-border px-1.5 py-1 text-xs" data-testid={`cf-up-${idx}`}>↑</button>
                  <button onClick={() => move(idx, 1)} className="rounded-md border border-border px-1.5 py-1 text-xs" data-testid={`cf-down-${idx}`}>↓</button>
                  <Switch checked={f.active !== false} onCheckedChange={() => toggleActive(idx)} data-testid={`cf-active-${idx}`} />
                  <button onClick={() => openEdit(f, idx)} className="flex h-9 w-9 items-center justify-center rounded-lg text-foreground hover:bg-white/5" data-testid={`cf-edit-${idx}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => removeField(idx)} className="flex h-9 w-9 items-center justify-center rounded-lg text-destructive hover:bg-destructive/10" data-testid={`cf-del-${idx}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-3 flex gap-2">
        <Button variant="secondary" className="h-11 flex-1 rounded-xl" onClick={openAdd} data-testid="cf-add"><Plus className="mr-1 h-4 w-4" />Add field</Button>
        <Button className="h-11 flex-1 rounded-xl" onClick={saveAll} disabled={saving || !dirty} data-testid="cf-save"><Save className="mr-1 h-4 w-4" />{saving ? 'Saving…' : (dirty ? 'Save changes' : 'Saved')}</Button>
      </div>

      <Drawer open={editOpen} onOpenChange={setEditOpen}>
        <DrawerContent className="bg-card">
          <div className="mx-auto my-2 h-1 w-10 rounded-full bg-border" />
          <DrawerHeader><DrawerTitle>{editIdx >= 0 ? 'Edit field' : 'Add field'}</DrawerTitle></DrawerHeader>
          <div className="max-h-[72vh] space-y-3 overflow-y-auto px-4 pb-6">
            <div>
              <Label className="text-xs">Field name</Label>
              <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="e.g. Tailor name" className="mt-1 h-11 rounded-xl" data-testid="cf-draft-name" />
            </div>
            <div>
              <Label className="text-xs">Type</Label>
              <div className="mt-1 grid grid-cols-3 gap-1.5">
                {FIELD_TYPES.map((t) => {
                  const Icon = t.icon; const active = draft.type === t.value;
                  return (
                    <button key={t.value} type="button" onClick={() => setDraft({ ...draft, type: t.value })} data-testid={`cf-draft-type-${t.value}`}
                      className={`flex flex-col items-center gap-1 rounded-xl border px-2 py-2.5 text-[11px] font-medium ${active ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card'}`}>
                      <Icon className="h-4 w-4" />{t.label}
                    </button>
                  );
                })}
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">{typeMeta(draft.type).hint}</p>
            </div>

            {draft.type === 'dropdown' && (
              <div>
                <Label className="text-xs">Options</Label>
                <div className="mt-1 flex gap-2">
                  <Input value={optText} onChange={(e) => setOptText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOption(); } }} placeholder="Add an option" className="h-11 rounded-xl" data-testid="cf-draft-option-input" />
                  <Button type="button" variant="secondary" className="h-11 rounded-xl" onClick={addOption} data-testid="cf-draft-option-add"><Plus className="h-4 w-4" /></Button>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(draft.options || []).map((o) => (
                    <span key={o} className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary px-2.5 py-1 text-xs" data-testid={`cf-draft-option-${o}`}>
                      {o}<button type="button" onClick={() => removeOption(o)} className="text-destructive"><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2 rounded-xl border border-border p-3">
              <Row label="Required">
                <Switch checked={draft.required} onCheckedChange={(v) => setDraft({ ...draft, required: v })} data-testid="cf-draft-required" />
              </Row>
              <Row label="Show on Inventory (Add/Edit stock)">
                <Switch checked={draft.applies_to.inventory} onCheckedChange={(v) => setDraft({ ...draft, applies_to: { ...draft.applies_to, inventory: v } })} data-testid="cf-draft-inventory" />
              </Row>
              <Row label="Show on Billing">
                <Switch checked={draft.applies_to.billing} onCheckedChange={(v) => setDraft({ ...draft, applies_to: { ...draft.applies_to, billing: v } })} data-testid="cf-draft-billing" />
              </Row>
            </div>

            <Button className="h-12 w-full rounded-xl" onClick={commitDraft} data-testid="cf-draft-save">{editIdx >= 0 ? 'Update field' : 'Add field'}</Button>
            <p className="text-center text-[11px] text-muted-foreground">Remember to tap <b>Save changes</b> afterwards to make it live.</p>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}

const Badge = ({ children, tone = 'slate' }) => {
  const tones = {
    slate: 'bg-secondary text-secondary-foreground',
    amber: 'bg-amber-500/15 text-amber-600',
    emerald: 'bg-emerald-500/15 text-emerald-600',
    indigo: 'bg-indigo-500/15 text-indigo-600',
  };
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${tones[tone]}`}>{children}</span>;
};

const Row = ({ label, children }) => (
  <div className="flex items-center justify-between gap-2"><span className="text-xs text-muted-foreground">{label}</span>{children}</div>
);
